import { createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { openEntityMemberInvitationApi } from "legal-entities/invitations/member-invitations/open/_api/open-entity-member-invitation-api-service";
import OpenEntityMemberInvitation from "legal-entities/invitations/member-invitations/open/open-entity-member-invitation";
import { OpenEntityMemberInvitationJSON } from "legal-entities/invitations/member-invitations/open/_schema/open-entity-member-invitation-schema";
import LegalEntity from "legal-entities/legal-entity";

export type OpenEntityMemberInvitationsState = {
  byId: {
    entries: Record<string, OpenEntityMemberInvitationJSON>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  byEntityId: {
    entries: Record<string, OpenEntityMemberInvitationJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

const initialState: OpenEntityMemberInvitationsState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byEntityId: {
    entries: {},
    loading: {},
    error: {},
  },
};

const openEntityMemberInvitationsSlice = createSlice({
  name: "openEntityMemberInvitations",
  initialState,
  reducers: {
    addOpenEntityMemberInvitation: (state, action: PayloadAction<OpenEntityMemberInvitation>) => {
      if (!action.payload.id?.value) return;
      state.byId.entries[action.payload.id?.value] = action.payload.toJSON();

      const entityId = action.payload.entity.id?.value;
      if (!entityId) return;
      state.byEntityId.entries[entityId] =
        state.byEntityId.entries[entityId]?.filter(
          (existingInvitation) => existingInvitation.id !== action.payload.id?.value,
        ) ?? [];
      state.byEntityId.entries[entityId].push(action.payload.toJSON());
    },
    removeOpenEntityMemberInvitation: (state, action: PayloadAction<OpenEntityMemberInvitation>) => {
      if (!action.payload.id?.value) return;
      delete state.byId.entries[action.payload.id?.value];

      const entityId = action.payload.entity.id?.value;
      if (!entityId) return;
      state.byEntityId.entries[entityId] =
        state.byEntityId.entries[entityId]?.filter(
          (existingInvitation) => existingInvitation.id !== action.payload.id?.value,
        ) ?? [];
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(openEntityMemberInvitationApi.endpoints.getOpenMemberInvitationById.matchPending, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.id?.value;
      if (!invitationId) return;
      state.byId.loading[invitationId] = true;
      state.byId.error[invitationId] = null;
    });
    builder.addMatcher(openEntityMemberInvitationApi.endpoints.getOpenMemberInvitationById.matchFulfilled, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.id?.value;
      if (!invitationId) return;
      state.byId.entries[invitationId] = action.payload.toJSON();
      state.byId.loading[invitationId] = false;
      state.byId.error[invitationId] = null;

      const entityId = action.payload.entity.id?.value;
      if (!entityId) return;
      state.byEntityId.entries[entityId] =
        state.byEntityId.entries[entityId]?.filter(
          (existingInvitation) => existingInvitation.id !== action.payload.id?.value,
        ) ?? [];
      state.byEntityId.entries[entityId].push(action.payload.toJSON());
    });
    builder.addMatcher(openEntityMemberInvitationApi.endpoints.getOpenMemberInvitationById.matchRejected, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.id?.value;
      if (!invitationId) return;
      state.byId.loading[invitationId] = false;
      state.byId.error[invitationId] = action.error;
    });

    builder.addMatcher(openEntityMemberInvitationApi.endpoints.getAllOpenEntityMemberInvitations.matchPending, (state, action) => {
      const entityId = action.meta.arg.originalArgs.entityId?.value;
      if (!entityId) return;
      state.byEntityId.loading[entityId] = true;
      state.byEntityId.error[entityId] = null;
    });
    builder.addMatcher(openEntityMemberInvitationApi.endpoints.getAllOpenEntityMemberInvitations.matchFulfilled, (state, action) => {
      const entityId = action.meta.arg.originalArgs.entityId?.value;
      if (!entityId) return;
      state.byEntityId.entries[entityId] = action.payload.map((invitation) => invitation.toJSON());
      state.byEntityId.loading[entityId] = false;
      state.byEntityId.error[entityId] = null;

      for (const invitation of action.payload) {
        if (!invitation.id?.value) continue;
        state.byId.entries[invitation.id?.value] = invitation.toJSON();
      }
    });
    builder.addMatcher(openEntityMemberInvitationApi.endpoints.getAllOpenEntityMemberInvitations.matchRejected, (state, action) => {
      const entityId = action.meta.arg.originalArgs.entityId?.value;
      if (!entityId) return;
      state.byEntityId.loading[entityId] = false;
      state.byEntityId.error[entityId] = action.error;
    });
  },
});

export const { addOpenEntityMemberInvitation, removeOpenEntityMemberInvitation } =
  openEntityMemberInvitationsSlice.actions;

export const getOpenEntityMemberInvitationById = (id: Guid) => (state: RootState) => {
  const json = state.openEntityMemberInvitations.byId.entries[id.value];
  return OpenEntityMemberInvitation.fromJSON(json);
};
export const getIsLoadingOpenEntityMemberInvitationById = (id: Guid) => (state: RootState) =>
  state.openEntityMemberInvitations.byId.loading[id.value];
export const getErrorLoadingOpenEntityMemberInvitationById = (id: Guid) => (state: RootState) =>
  state.openEntityMemberInvitations.byId.error[id.value];

export const getOpenEntityMemberInvitationsForLegalEntity = (entity: LegalEntity) => (state: RootState) =>
  state.openEntityMemberInvitations.byEntityId.entries[entity.id?.value];
export const getIsLoadingOpenEntityMemberInvitationsForLegalEntity = (entity: LegalEntity) => (state: RootState) =>
  state.openEntityMemberInvitations.byEntityId.loading[entity.id?.value];
export const getErrorLoadingOpenEntityMemberInvitationsForLegalEntity = (entity: LegalEntity) => (state: RootState) =>
  state.openEntityMemberInvitations.byEntityId.error[entity.id?.value];

export const getUserEntityMemberInvitationById = (id: Guid) => (state: RootState) =>
  state.userEntityMemberInvitations.byId.entries[id.value];
export const getIsLoadingUserEntityMemberInvitationById = (id: Guid) => (state: RootState) =>
  state.userEntityMemberInvitations.byId.loading[id.value];
export const getErrorLoadingUserEntityMemberInvitationById = (id: Guid) => (state: RootState) =>
  state.userEntityMemberInvitations.byId.error[id.value];

export default openEntityMemberInvitationsSlice;
